import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Form,
  toast,
} from '@qodo/design-system';
import { LOCAL_STORAGE_KEYS } from 'consts/localStorage';
import { useUser } from 'hooks/useUser';
import { X } from 'lucide-react';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { Image } from '../../Image';
import { getInstallationGuideCarouselSteps } from '../configuration';
import { FORM_IDS } from '../consts';
import { useGithubInstallation } from '../githubInstallationContext';
import { CarouselStepConfig } from '../types';
import {
  buildManifestJson,
  buildManifestState,
  createAndSubmitManifestForm,
  getOrgNameFromUrl,
} from '../utils';

export const InstallApp = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const { setIsPending, setIsNextEnabled } = useMultiStepDialog();
  const {
    organizationUrl,
    isInstallAppSuggestionDismissed,
    setIsInstallAppSuggestionDismissed,
    webhookUrl,
    isWebhookUrlLoading,
  } = useGithubInstallation();
  const { user } = useUser();
  const workspaceId = user?.workspaceId;

  const methods = useForm();

  const spawnInstallApp = spawn('dialogs.github-installation.install-app');
  const installationGuideCarouselSteps = getInstallationGuideCarouselSteps();

  useEffect(() => {
    setIsNextEnabled(
      !!organizationUrl && !!workspaceId && !isWebhookUrlLoading,
    );
  }, [organizationUrl, workspaceId, isWebhookUrlLoading, setIsNextEnabled]);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const onSubmit = async () => {
    if (!workspaceId) {
      console.warn('No workspace ID found for user, cannot submit manifest');
      toast.error(spawnInstallApp('errors.missing-workspace'));

      return;
    }

    if (isWebhookUrlLoading) {
      console.warn(
        'Webhook URL is still loading, cannot submit manifest with fallback',
      );
      toast.error(spawnInstallApp('errors.webhook-url-loading'));

      return;
    }

    setIsPending(true);

    try {
      const url = new URL(organizationUrl);
      const orgName = getOrgNameFromUrl(url);

      const state = buildManifestState(workspaceId, organizationUrl);

      const manifest = buildManifestJson(orgName, webhookUrl);

      // Store state object for redirect validation (includes secret + workspace_id)
      localStorage.setItem(LOCAL_STORAGE_KEYS.GITHUB_INSTALLATION_STATE, state);

      // Create and submit hidden form to GitHub with state
      // This will cause full page navigation, as required (user should leave the SPA)
      createAndSubmitManifestForm(manifest, state, {
        orgName,
        organizationUrl,
      });

      // Note: Code after this point won't execute due to page navigation
    } catch (error) {
      console.error('Failed to submit manifest:', error);
      setIsPending(false);
      // TODO: Show error to user (can be done in future phase)
      throw error;
    }
  };

  const renderCarouselItem = (step: CarouselStepConfig) => {
    const title = spawnInstallApp(`${step.id}.title`);

    return (
      <CarouselItem key={step.id} className="basis-full">
        <div className="flex flex-col gap-2">
          <div className="text-text-general-neutral-mid">
            {spawnInstallApp(`${step.id}.description`)}
          </div>
          <Image
            source={step.image}
            alt={title}
            title={title}
            className="w-full rounded-md opacity-80"
          />
        </div>
      </CarouselItem>
    );
  };

  const renderCarouselIndicator = (step: CarouselStepConfig, index: number) => (
    <CarouselIndicator
      key={step.id}
      index={index}
      isCurrent={index === current}
      className="uppercase"
    >
      {index + 1}. {spawnInstallApp(`${step.id}.title`)}
    </CarouselIndicator>
  );

  const renderCarousel = () => (
    <Carousel setApi={setApi} className="w-full">
      <CarouselContent>
        {installationGuideCarouselSteps.map(renderCarouselItem)}
      </CarouselContent>
      <CarouselFooter>
        <CarouselPrevious type="button" />
        <div className="flex items-center justify-center gap-6 font-mono text-sm uppercase">
          {installationGuideCarouselSteps.map(renderCarouselIndicator)}
        </div>
        <CarouselNext type="button" />
      </CarouselFooter>
    </Carousel>
  );

  const renderSuggestionIfNeeded = () => {
    if (isInstallAppSuggestionDismissed) {
      return <></>;
    }

    return (
      <div className="flex flex-row justify-between items-center gap-4 border border-border-panel-emphasis rounded-lg p-4 text-xs text-text-general-neutral-mid">
        {spawnInstallApp('suggestion')}
        <Button
          size="xs"
          aria-label={spawnInstallApp('suggestion-dismiss-aria-label')}
          variant="ghost"
          className="p-0"
          onClick={() => setIsInstallAppSuggestionDismissed(true)}
        >
          <X className="stroke-icon-action-neutral-idle size-4" />
        </Button>
      </div>
    );
  };

  return (
    <Step
      title={spawnInstallApp('title')}
      description={spawnInstallApp('description')}
    >
      {renderSuggestionIfNeeded()}
      <Form
        {...({ id: FORM_IDS.INSTALL_APP } as Record<string, string>)}
        methods={methods}
        onSubmit={onSubmit}
      >
        {renderCarousel()}
      </Form>
    </Step>
  );
};
